export interface StatusMeta {
  color: string; // tailwind color name without shade, e.g., 'yellow', 'blue'
  labelKey: string; // i18n key for translation
}

// Meta mapping for statuses (keys are UPPERCASE)
export const STATUS_META: Record<string, StatusMeta> = {
  DRAFT: { color: "gray", labelKey: "Draft" },
  SCHEDULED: { color: "emerald", labelKey: "Scheduled" },
  PUBLISHING: { color: "amber", labelKey: "Publishing" },
  PUBLISHED: { color: "blue", labelKey: "Published" },
  FAILED: { color: "red", labelKey: "Failed" },
  CANCELLED: { color: "rose", labelKey: "Cancelled" },
  RETRYING: { color: "yellow", labelKey: "Retrying" },
};

// Base options (values follow current filtering usage - lowercase for filters, plus 'All')
export const STATUS_VALUES = [
  "All",
  "scheduled",
  "draft",
  "published",
  "publishing",
  "cancelled",
  "failed",
  "retrying",
] as const;

export type StatusValue = (typeof STATUS_VALUES)[number];

export const buildStatusOptions = (t: (s: string, opts?: any) => string) =>
  STATUS_VALUES.map((value) => ({
    value,
    label: value === "All" ? t("All Status") : t(value.charAt(0).toUpperCase() + value.slice(1)),
  }));

// Helper to get translated label from status value (lowercase or uppercase)
export const getStatusLabel = (
  value: string | undefined,
  t: (s: string, opts?: any) => string
): string => {
  if (!value) return t("All Status");
  const v = String(value);
  if (v.toLowerCase() === "all") return t("All Status");
  return t(v.charAt(0).toUpperCase() + v.slice(1).toLowerCase());
};

// Helper to get tailwind classes for a given UPPERCASE status
export const getStatusStyle = (statusUpper: string) => {
  const meta = STATUS_META[statusUpper] || ({ color: "gray", labelKey: "Draft" } as StatusMeta);

  const STYLES: Record<string, { bgClass: string; textClass: string; dotClass: string }> = {
    DRAFT: {
      bgClass: "bg-gray-100",
      textClass: "text-gray-800",
      dotClass: "bg-gray-600",
    },
    SCHEDULED: {
      bgClass: "bg-emerald-100",
      textClass: "text-emerald-800",
      dotClass: "bg-emerald-800", // darker dot like yellow case
    },
    PUBLISHING: {
      bgClass: "bg-amber-100",
      textClass: "text-amber-800",
      dotClass: "bg-amber-600",
    },
    PUBLISHED: {
      bgClass: "bg-blue-100",
      textClass: "text-blue-800",
      dotClass: "bg-blue-600",
    },
    FAILED: {
      bgClass: "bg-red-100",
      textClass: "text-red-800",
      dotClass: "bg-red-600",
    },
    CANCELLED: {
      bgClass: "bg-rose-100",
      textClass: "text-rose-800",
      dotClass: "bg-rose-600",
    },
    RETRYING: {
      bgClass: "bg-orange-100",
      textClass: "text-orange-800",
      dotClass: "bg-orange-600",
    },
  };

  const style = STYLES[statusUpper] || STYLES.DRAFT;
  return { ...style, labelKey: meta.labelKey };
};
